import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './EditPlan.css';
import WorkoutSessionCard from '../components/WorkoutSessionCard';
import {
  type WorkoutDay,
  blankSession,
  applyModalityChange,
  DAY_NUMBER_TO_NAME,
  dayNameToNumber,
} from '../lib/workoutOptions';

// ─── Types ────────────────────────────────────────────────────────────────────

interface WorkoutTemplate {
  id: string;
  nanoid: string;
  name: string;
  modality: string;
  difficulty: string | null;
  duration_minutes: number | null;
  delivery_time: string | null;
  day_slots: { week_number: number; day_number: number; day_name: string }[];
}

interface Plan {
  id: string;
  nanoid: string;
  name: string;
  description: string | null;
  focus: string | null;
  duration_weeks: number | null;
}

// One editable session = one workout template on one day-of-week. A template that
// recurs on multiple weekdays becomes multiple sessions. workout_id is null for
// newly-added sessions (the backend AI-generates a workout for them).
interface EditSession extends WorkoutDay {
  uid: number;
  workout_id: string | null;
  workout_nanoid: string | null;
  original_day_number: number | null;
  original: { name: string; modality: string; difficulty: string; duration_minutes: number | null; time: string } | null;
}

const SCOPE_OPTIONS = [
  { label: 'Remaining Plan', value: 'remaining',  desc: 'All future workouts' },
  { label: 'This Week',      value: 'this_week',  desc: 'Current week only'   },
  { label: 'Next Week',      value: 'next_week',  desc: 'Upcoming week only'  },
];

const INTENSITY_OPTIONS = [
  { label: '↓ Lighter',    value: 'lighter', desc: 'Fewer sets & reps' },
  { label: 'Keep current', value: null,      desc: 'No volume change'  },
  { label: '↑ Harder',     value: 'harder',  desc: 'More sets & reps'  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function normalizeTime(t: string | null): string {
  if (!t) return '';
  return t.slice(0, 5); // "08:00:00" → "08:00"
}

let uidCounter = 0;
const nextUid = () => ++uidCounter;

// Build the editable session list from the plan's templates. One session per
// (template, unique weekday). Difficulty/duration/time pre-filled from the template.
function templatesToSessions(templates: WorkoutTemplate[]): EditSession[] {
  const sessions: EditSession[] = [];
  for (const t of templates) {
    const dayNumbers = [...new Set(t.day_slots.map(s => s.day_number))].sort((a, b) => a - b);
    for (const dn of dayNumbers) {
      const time = normalizeTime(t.delivery_time);
      const difficulty = (t.difficulty as WorkoutDay['difficulty']) ?? 'beginner';
      sessions.push({
        ...blankSession(),
        day:              DAY_NUMBER_TO_NAME[dn] ?? '',
        name:             t.name,
        modality:         t.modality,
        difficulty,
        duration_minutes: t.duration_minutes ?? 45,
        time,
        uid:                 nextUid(),
        workout_id:          t.id,
        workout_nanoid:      t.nanoid,
        original_day_number: dn,
        original: {
          name:             t.name,
          modality:         t.modality,
          difficulty,
          duration_minutes: t.duration_minutes,
          time,
        },
      });
    }
  }
  return sessions;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EditPlan() {
  const { nanoid } = useParams<{ nanoid: string }>();
  const navigate = useNavigate();

  const [plan, setPlan] = useState<Plan | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Plan-level name + dynamic session list
  const [planName, setPlanName] = useState('');
  const [sessions, setSessions] = useState<EditSession[]>([]);
  // Snapshot of sessions as loaded — used to detect deletes on save.
  const [originals, setOriginals] = useState<{ uid: number; workout_id: string; day_number: number }[]>([]);

  // Advanced / AI adjustments
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [scope, setScope] = useState<string>('remaining');
  const [intensity, setIntensity] = useState<string | null>(null);
  const [freeText, setFreeText] = useState('');

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!nanoid) return;
    fetch(`/api/plans/${nanoid}`)
      .then(r => r.json())
      .then(data => {
        if (!data.success) throw new Error(data.error || 'Failed to load plan');
        setPlan(data.plan);
        setPlanName(data.plan.name ?? '');
        const tmpl: WorkoutTemplate[] = (data.workout_templates ?? [])
          .filter((t: WorkoutTemplate) => t.day_slots.length > 0);
        const built = templatesToSessions(tmpl);
        setSessions(built);
        setOriginals(
          built
            .filter(s => s.workout_id)
            .map(s => ({ uid: s.uid, workout_id: s.workout_id!, day_number: s.original_day_number! })),
        );
      })
      .catch(err => setError(err.message))
      .finally(() => setLoading(false));
  }, [nanoid]);

  // ── Session list helpers (mirror the builder) ────────────────────────────
  const addSession = () =>
    setSessions(prev => [...prev, {
      ...blankSession(),
      uid: nextUid(), workout_id: null, workout_nanoid: null, original_day_number: null, original: null,
    }]);

  const removeSession = (i: number) => setSessions(prev => prev.filter((_, idx) => idx !== i));

  // Duplicating an existing workout to another day creates a brand-new session
  // (its own AI-generated workout), matching the builder's clone-config behavior.
  const duplicateSession = (i: number) =>
    setSessions(prev => [
      ...prev.slice(0, i + 1),
      { ...prev[i], day: '', uid: nextUid(), workout_id: null, workout_nanoid: null, original_day_number: null, original: null },
      ...prev.slice(i + 1),
    ]);

  const setField = <K extends keyof WorkoutDay>(i: number, field: K, value: WorkoutDay[K]) =>
    setSessions(prev => prev.map((s, idx) => idx === i ? { ...s, [field]: value } : s));

  const setModality = (i: number, mod: string) =>
    setSessions(prev => prev.map((s, idx) => idx === i ? applyModalityChange(s, mod) as EditSession : s));

  const togglePill = (i: number, field: 'strength_focus' | 'equipment', val: string) =>
    setField(i, field, sessions[i][field] === val ? '' : val);

  // ── Change detection ─────────────────────────────────────────────────────
  function buildPayload() {
    const currentUids = new Set(sessions.map(s => s.uid));

    // Deletes: an originally-loaded session no longer present in the list.
    const day_deletes = originals
      .filter(o => !currentUids.has(o.uid))
      .map(o => ({ workout_id: o.workout_id, day_number: o.day_number }));

    // Adds: new sessions with a chosen modality + day.
    const day_adds = sessions
      .filter(s => !s.workout_id && s.modality && s.day)
      .map(s => ({
        day_number:       dayNameToNumber(s.day),
        name:             s.name.trim() || null,
        modality:         s.modality,
        difficulty:       s.difficulty,
        duration_minutes: s.duration_minutes,
        time:             s.time || null,
        strength_focus:   s.strength_focus || null,
        equipment:        s.equipment || null,
        notes:            s.notes || null,
        other_notes:      s.other_notes || null,
      }));

    // Moves: existing session whose weekday changed.
    const day_moves = sessions
      .filter(s => s.workout_id && s.original_day_number != null && s.day &&
                   dayNameToNumber(s.day) !== s.original_day_number)
      .map(s => ({
        workout_id:      s.workout_id!,
        old_day_number:  s.original_day_number!,
        new_day_number:  dayNameToNumber(s.day),
      }));

    // Property changes: dedupe by workout_id (a workout may span several days).
    const seen = new Set<string>();
    const workout_changes: Record<string, unknown>[] = [];
    const time_changes: { workout_nanoid: string; new_time: string }[] = [];
    for (const s of sessions) {
      if (!s.workout_id || !s.original || seen.has(s.workout_id)) continue;
      seen.add(s.workout_id);
      workout_changes.push({
        workout_id:           s.workout_id,
        workout_nanoid:       s.workout_nanoid,
        workout_name:         s.name.trim() || s.original.name,
        old_modality:         s.original.modality,
        new_modality:         s.modality,
        new_difficulty:       s.difficulty,
        new_duration_minutes: s.duration_minutes !== s.original.duration_minutes ? s.duration_minutes : null,
        new_notes:            s.notes || '',
        new_strength_focus:   s.strength_focus || '',
        new_equipment:        s.equipment || '',
      });
      if (s.workout_nanoid && s.time && s.time !== s.original.time) {
        time_changes.push({ workout_nanoid: s.workout_nanoid, new_time: s.time });
      }
    }

    return { day_deletes, day_adds, day_moves, workout_changes, time_changes };
  }

  const hasChanges = () => {
    if (intensity !== null) return true;
    if (freeText.trim()) return true;
    if (plan && planName.trim() !== plan.name) return true;
    const p = buildPayload();
    if (p.day_adds.length || p.day_deletes.length || p.day_moves.length) return true;
    for (const c of p.workout_changes) {
      const orig = sessions.find(s => s.workout_id === c.workout_id)?.original;
      if (!orig) continue;
      if (c.workout_name !== orig.name) return true;
      if (c.new_modality !== orig.modality) return true;
      if (c.new_difficulty !== orig.difficulty) return true;
      if (c.new_duration_minutes !== null) return true;
      if (c.new_notes || c.new_strength_focus || c.new_equipment) return true;
    }
    if (p.time_changes.length) return true;
    return false;
  };

  const needsAi = () => {
    const p = buildPayload();
    if (p.day_adds.length) return true;
    return p.workout_changes.some(c => {
      const orig = sessions.find(s => s.workout_id === c.workout_id)?.original;
      return orig && c.new_modality !== orig.modality;
    });
  };

  const handleSubmit = async () => {
    if (!nanoid) return;
    setSubmitting(true);
    setSubmitError(null);

    const { day_deletes, day_adds, day_moves, workout_changes, time_changes } = buildPayload();

    try {
      const res = await fetch(`/api/plans/${nanoid}/modify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          scope,
          intensity: intensity ?? null,
          plan_name: planName.trim() || null,
          workout_changes,
          time_changes,
          day_adds,
          day_moves,
          day_deletes,
          free_text: freeText.trim() || null,
        }),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error || 'Update failed');
      setSubmitted(true);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // ── Loading / error / success states ─────────────────────────────────────

  if (loading) {
    return (
      <div className="ep-page">
        <div className="ep-state-screen">
          <div className="ep-spinner" />
          <p className="ep-state-msg">Loading your plan...</p>
        </div>
      </div>
    );
  }

  if (error || !plan) {
    return (
      <div className="ep-page">
        <div className="ep-state-screen">
          <p className="ep-state-msg ep-state-error">{error || 'Plan not found.'}</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="ep-page">
        <div className="ep-state-screen">
          <div className="ep-success-icon">✓</div>
          <h2 className="ep-success-title">Plan updated!</h2>
          <p className="ep-state-msg">Your changes are being applied. It may take a moment to regenerate exercises.</p>
          <button
            className="ep-btn ep-btn-primary"
            onClick={() => navigate(`/plan/${nanoid}`)}
          >
            View Plan
          </button>
        </div>
      </div>
    );
  }

  // ── Edit form ─────────────────────────────────────────────────────────────

  return (
    <div className="ep-page">
      <div className="ep-root">
        <div className="ep-container">

          {/* ── Back + header ────────────────────────────────────── */}
          <div className="ep-back-row">
            <button className="ep-back-btn" onClick={() => navigate(`/plan/${nanoid}`)}>
              ← Back to plan
            </button>
          </div>

          <div className="ep-header">
            <h1 className="ep-title">Edit Plan</h1>
          </div>

          {/* ── Plan name ─────────────────────────────────────────── */}
          <div className="ep-section">
            <h2 className="ep-section-title">Plan name</h2>
            <input
              type="text"
              className="ep-name-input"
              value={planName}
              onChange={e => setPlanName(e.target.value)}
              placeholder="My Workout Plan"
              maxLength={120}
            />
          </div>

          {/* ── Workout sessions (builder-style) ──────────────────── */}
          <div className="ep-section">
            <h2 className="ep-section-title">Your Workouts</h2>
            <p className="ep-section-hint">
              Add, remove, or move workouts to different days. Changes apply to all remaining weeks.
            </p>

            <div className="wb-session-list">
              {sessions.map((session, i) => (
                <WorkoutSessionCard
                  key={session.uid}
                  session={session}
                  onField={(field, value) => setField(i, field, value)}
                  onModality={mod => setModality(i, mod)}
                  onTogglePill={(field, val) => togglePill(i, field, val)}
                  onRemove={() => removeSession(i)}
                  onDuplicate={() => duplicateSession(i)}
                />
              ))}
            </div>

            <button className="wb-add-btn" onClick={addSession}>+ Add Workout Session</button>
          </div>

          {/* ── Advanced / AI adjustments ─────────────────────────── */}
          <div className="ep-section">
            <button
              className="ep-advanced-toggle"
              onClick={() => setAdvancedOpen(o => !o)}
              aria-expanded={advancedOpen}
            >
              <span>Advanced · AI adjustments</span>
              <span className="ep-advanced-chevron">{advancedOpen ? '▾' : '▸'}</span>
            </button>

            {advancedOpen && (
              <div className="ep-advanced-body">
                {/* Scope */}
                <div className="ep-subsection">
                  <h3 className="ep-subsection-title">Apply intensity / notes to</h3>
                  <div className="ep-scope-row">
                    {SCOPE_OPTIONS.map(opt => (
                      <button
                        key={opt.value}
                        className={`ep-scope-btn${scope === opt.value ? ' ep-scope-btn--active' : ''}`}
                        onClick={() => setScope(opt.value)}
                      >
                        <span className="ep-scope-label">{opt.label}</span>
                        <span className="ep-scope-desc">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Intensity */}
                <div className="ep-subsection">
                  <h3 className="ep-subsection-title">Intensity</h3>
                  <p className="ep-section-hint">Scales sets, reps, and duration up or down.</p>
                  <div className="ep-intensity-row">
                    {INTENSITY_OPTIONS.map(opt => (
                      <button
                        key={String(opt.value)}
                        className={`ep-intensity-btn${intensity === opt.value ? ' ep-intensity-btn--active' : ''}`}
                        onClick={() => setIntensity(opt.value)}
                      >
                        <span className="ep-intensity-label">{opt.label}</span>
                        <span className="ep-intensity-desc">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Free text */}
                <div className="ep-subsection">
                  <h3 className="ep-subsection-title">Anything else?</h3>
                  <p className="ep-section-hint">Injuries, equipment you don't have, specific requests.</p>
                  <textarea
                    className="ep-textarea"
                    rows={4}
                    value={freeText}
                    onChange={e => setFreeText(e.target.value)}
                    placeholder="e.g. I hurt my knee — avoid jumping. I don't have a pull-up bar."
                    maxLength={500}
                  />
                  {freeText.length > 0 && (
                    <p className="ep-char-count">{freeText.length}/500</p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* ── Submit ────────────────────────────────────────────── */}
          {submitError && (
            <p className="ep-submit-error">{submitError}</p>
          )}

          <button
            className="ep-btn ep-btn-primary ep-btn-submit"
            onClick={handleSubmit}
            disabled={submitting || !hasChanges()}
          >
            {submitting ? 'Updating plan…' : 'Update Plan'}
          </button>

          {needsAi() && (
            <p className="ep-submit-hint">
              Adding workouts or changing a workout's type uses AI and may take 15–30 seconds.
            </p>
          )}

        </div>
      </div>

      <footer className="ep-footer">
        <div className="ep-footer-inner">
          <span className="ep-footer-brand">Mosaime</span>
          <p className="ep-footer-copy">&copy; {new Date().getFullYear()} Mosaime. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
